import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Clock, Calendar, Share2, Facebook, Linkedin, Link2, ChevronRight, LogIn, Home, MessageCircle, Send, ImagePlus, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import dcLogo from "@/assets/dc-logo.png";
import { isNativePlatform } from "@/lib/platform";
import { buildShareUrl } from "@/lib/share-url";
import { nativeShare, openExternalUrl, copyToClipboard } from "@/lib/native-share";
import { shareWithImages } from "@/lib/share-image";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const PinterestIcon = ({ className = "h-3.5 w-3.5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.43.22-.93 1.4-5.94 1.4-5.94s-.36-.72-.36-1.78c0-1.67.97-2.92 2.17-2.92 1.03 0 1.52.77 1.52 1.7 0 1.03-.66 2.58-1 4.02-.28 1.2.6 2.18 1.79 2.18 2.15 0 3.8-2.27 3.8-5.54 0-2.9-2.08-4.92-5.05-4.92-3.44 0-5.46 2.58-5.46 5.25 0 1.04.4 2.16.9 2.76.1.12.11.23.08.35-.09.36-.28 1.2-.32 1.36-.05.22-.17.27-.4.16-1.5-.7-2.43-2.88-2.43-4.64 0-3.78 2.75-7.25 7.92-7.25 4.16 0 7.39 2.96 7.39 6.92 0 4.13-2.6 7.46-6.22 7.46-1.21 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15A12 12 0 1 0 12 0z"/></svg>
);
import { Seo } from "@/components/Seo";
import BlogAppAccess from "@/components/blog/BlogAppAccess";

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  cover_image_url: string | null;
  category_id: string | null;
  author_name: string;
  published_at: string | null;
  reading_time_minutes: number;
  meta_description: string | null;
  blog_categories?: BlogCategory | null;
}

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    if (slug) loadPost(slug);
    supabase.auth.getSession().then(({ data }) => setIsLoggedIn(!!data.session));
  }, [slug]);

  const loadPost = async (slug: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*, blog_categories(*)")
        .eq("slug", slug)
        .eq("is_published", true)
        .single();

      if (error || !data) {
        navigate("/blog");
        return;
      }
      setPost(data as any);

      // Set dynamic OG meta tags
      const metaTags: Record<string, string> = {
        "og:title": data.title,
        "og:description": data.meta_description || data.excerpt || "",
        "og:image": data.cover_image_url || "",
        "og:type": "article",
        "twitter:title": data.title,
        "twitter:description": data.meta_description || data.excerpt || "",
        "twitter:image": data.cover_image_url || "",
      };
      Object.entries(metaTags).forEach(([property, content]) => {
        if (!content) return;
        const attr = property.startsWith("twitter:") ? "name" : "property";
        let el = document.querySelector(`meta[${attr}="${property}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute(attr, property);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      });
      document.title = `${data.title} — Decor Canvas`;

      // Load related posts
      if (data.category_id) {
        const { data: related } = await supabase
          .from("blog_posts")
          .select("*, blog_categories(*)")
          .eq("is_published", true)
          .eq("category_id", data.category_id)
          .neq("id", data.id)
          .order("published_at", { ascending: false })
          .limit(3);
        setRelatedPosts((related as any[]) || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const ogShareUrl = post?.slug
    ? buildShareUrl(`/blog/${post.slug}`)
    : (typeof window !== "undefined" ? window.location.href : "");
  const shareTitle = post?.title || "";

  const handleShare = (platform: string) => {
    // Native: use the OS share sheet directly so users can pick any installed
    // app (Messages, WhatsApp, X, etc.) instead of trying to load the web URL
    // inside the app's WebView.
    if (isNativePlatform()) {
      void nativeShare({ title: shareTitle, text: shareTitle, url: ogShareUrl });
      return;
    }

    const encodedUrl = encodeURIComponent(ogShareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    const urls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}&summary=${encodeURIComponent(post?.excerpt || post?.meta_description || "")}&source=Decor%20Canvas`,
      whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
      substack: `https://substack.com/note?url=${encodedUrl}&title=${encodedTitle}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodeURIComponent(post?.cover_image_url || "")}&description=${encodedTitle}`,
    };
    if (urls[platform]) void openExternalUrl(urls[platform]);
  };

  const [sharingImage, setSharingImage] = useState(false);
  const sanitize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const handleShareWithImage = async () => {
    if (!post) return;
    setSharingImage(true);
    try {
      const result = await shareWithImages({
        images: post.cover_image_url ? [post.cover_image_url] : [],
        url: ogShareUrl,
        title: shareTitle,
        text: shareTitle,
        filenameBase: `decor-canvas-${sanitize(post.slug)}`,
      });
      if (result === "copied") toast.success("Link copied — paste it into your post");
      else if (result === "url" && post.cover_image_url) {
        toast.message("Shared the link (image couldn't be attached)");
      }
    } catch {
      // user cancelled or share failed silently
    } finally {
      setSharingImage(false);
    }
  };

  const hasNativeShare = isNativePlatform() || (typeof navigator !== "undefined" && !!navigator.share);

  const handleCopyLink = async () => {
    const ok = await copyToClipboard(ogShareUrl);
    if (ok) toast.success("Link copied!");
    else toast.error("Failed to copy");
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-20 space-y-6">
          <Skeleton className="w-full h-[400px] rounded-2xl" />
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-[300px] w-full" />
        </div>
      </div>
    );
  }

  if (!post) return null;

  const categoryName = (post as any).blog_categories?.name || null;

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={`${post?.title ?? "Article"} | Decor Canvas Blog`}
        description={post?.meta_description || post?.excerpt || "Interior design article from Decor Canvas — AI room redesign tips, style guides, and inspiration."}
        canonicalPath={`/blog/${post?.slug ?? ""}`}
        ogImage={post?.cover_image_url || undefined}
        jsonLd={post ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.meta_description || post.excerpt || undefined,
          image: post.cover_image_url || undefined,
          author: { "@type": "Person", name: post.author_name },
          publisher: { "@type": "Organization", name: "Decor Canvas", logo: { "@type": "ImageObject", url: "https://decorcanvas.design/images/dc-logo.webp" } },
          datePublished: post.published_at || undefined,
          mainEntityOfPage: `https://decorcanvas.design/blog/${post.slug}`,
        } : undefined}
      />
      {/* Header — mobile mirrors Notifications screen exactly */}
      {isNativePlatform() ? (
        <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10 safe-area-inset-top">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl sm:text-2xl font-bold truncate flex-1 min-w-0">
                {post.title}
              </h1>
            </div>
          </div>
        </header>
      ) : (
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img src={dcLogo} alt="Decor Canvas" className="h-8 w-8 rounded-lg" />
              <span className="text-lg font-bold tracking-tight text-foreground">Decor Canvas</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              {isLoggedIn ? (
                <Button size="sm" onClick={() => navigate("/designer")} className="bg-primary text-primary-foreground">
                  <Home className="h-4 w-4 mr-1" />
                  Home
                </Button>
              ) : (
                <Button size="sm" onClick={() => navigate("/auth")} className="bg-primary text-primary-foreground">
                  <LogIn className="h-4 w-4 mr-1" />
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </header>
      )}

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Cover Image */}
        {post.cover_image_url && (
          <div className="rounded-2xl overflow-hidden mb-8 aspect-[21/9]">
            <img
              src={post.cover_image_url}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {categoryName && (
            <Badge className="bg-primary/10 text-primary border-primary/20">{categoryName}</Badge>
          )}
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {post.reading_time_minutes} min read
          </span>
          {post.published_at && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              {format(new Date(post.published_at), "MMMM d, yyyy")}
            </span>
          )}
          <span className="text-sm text-muted-foreground">by {post.author_name}</span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
          {post.title}
        </h1>

        {post.excerpt && (
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">{post.excerpt}</p>
        )}

        <BlogAppAccess className="mb-8" />

        {/* Share Bar */}
        <div className="mb-10 pb-8 border-b border-border">
          <div className="flex items-center gap-2 flex-wrap">
            <Share2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground mr-2">Share</span>

            {hasNativeShare && (
              <Button
                variant="default"
                size="sm"
                className="h-8 rounded-full px-3 gap-1.5"
                onClick={handleShareWithImage}
                disabled={sharingImage}
                title="Share with cover image"
              >
                {sharingImage ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                <span className="text-xs">{sharingImage ? "Preparing…" : "Share with image"}</span>
              </Button>
            )}

            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleShare("twitter")} title="X (Twitter) — cover shows in link preview">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleShare("facebook")} title="Facebook — cover shows in link preview">
              <Facebook className="h-3.5 w-3.5" />
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleShare("linkedin")}>
                  <Linkedin className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Opens LinkedIn's "Share an article" composer with the cover preview.</TooltipContent>
            </Tooltip>
            {post.cover_image_url && (
              <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleShare("pinterest")} title="Pin with cover image">
                <PinterestIcon />
              </Button>
            )}
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleShare("reddit")} title="Reddit">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleShare("telegram")} title="Telegram">
              <Send className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleShare("whatsapp")} title="WhatsApp">
              <MessageCircle className="h-3.5 w-3.5" />
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => handleShare("substack")}>
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/></svg>
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Opens a new Substack Note with this article attached. Substack doesn't allow pre-filling a full article from a link — paste it into your draft to publish as one.
              </TooltipContent>
            </Tooltip>
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={handleCopyLink} title="Copy link">
              <Link2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
            The cover image attaches automatically via the link preview on X, Facebook, LinkedIn, Reddit, WhatsApp, Telegram, and Substack.
            {hasNativeShare ? " Use \"Share with image\" to post the cover directly to Instagram, Threads, Stories, or Messages." : ""}
            {" "}Pinterest uses the cover as the pin image.
          </p>
        </div>


        {/* Content */}
        <div
          className="prose prose-lg max-w-none prose-headings:text-foreground prose-p:text-foreground/80 prose-a:text-primary prose-img:rounded-xl prose-strong:text-foreground"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        <BlogAppAccess withIntro className="mt-12 pt-8 border-t border-border" />

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-16 pt-10 border-t border-border">
            <h2 className="text-2xl font-bold text-foreground mb-6">Related Posts</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((rp) => (
                <article
                  key={rp.id}
                  className="group rounded-2xl overflow-hidden border border-border bg-card shadow-card hover:shadow-elegant transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(`/blog/${rp.slug}`)}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    {rp.cover_image_url ? (
                      <img src={rp.cover_image_url} alt={rp.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent/10" />
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-foreground line-clamp-2 group-hover:text-primary transition-colors mb-2">
                      {rp.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />{rp.reading_time_minutes} min
                      </span>
                      <ChevronRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPostPage;
